<template>
  <div>
    <component :is="layoutComponent" v-if="routerReady">
      <router-view />
    </component>
    <div class="app-version">{{ appVersion }}</div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { Capacitor } from '@capacitor/core';
import DefaultLayout from '@/layouts/default.vue';
import ProfileLayout from '@/layouts/profile.vue';
import ProfileLock from '@/layouts/profile-lock.vue';
import RegionSelectorLayout from '@/layouts/region-selector.vue';
import HomepageLayout from '@/layouts/homepage.vue';
import ServicePageLayout from '@/layouts/service-page.vue';
import InfoPageLayout from '@/layouts/info-page.vue';
import { useAppLock } from '@/feature/app-lock/composables/useAppLock';
import { useInactivityTimer } from '@/feature/app-lock/composables/useInactivityTimer';

const inactivityTimer = useInactivityTimer();

export default defineComponent({
  name: 'App',
  components: {
    DefaultLayout,
    ProfileLayout,
    ProfileLock,
    RegionSelectorLayout,
    HomepageLayout,
    ServicePageLayout,
    InfoPageLayout
  },
  metaInfo: {
    title: 'uLékaře.cz',
    meta: [
      { charset: 'utf-8' },
      { vmid: 'og:image', name: 'og:image', content: '/share-login.jpg' },
      { vmid: 'image', name: 'image', property: 'og:image', content: '/share-login.jpg' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1, viewport-fit=cover' },
      { name: 'facebook-domain-verification', content: import.meta.env.VITE_FB_DOMAIN_VERIFICATION }
    ],
    link: [{ rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' }],
    script: [
      {
        src: `https://maps.googleapis.com/maps/api/js?key=${
          import.meta.env.VITE_GOOGLE_MAPS_KEY
        }&libraries=places`,
        async: 'true'
      },
      {
        type: 'text/javascript',
        src: '/unsupportedBrowserModal.js',
        body: true
      },
      {
        type: 'text/javascript',
        src: '/clarity.js'
      }
    ]
  },
  data() {
    return {
      routerReady: false
    };
  },
  computed: {
    layoutComponent() {
      return this.$route.meta.layout || 'DefaultLayout';
    },
    appVersion() {
      return import.meta.env.VITE_APP_VERSION;
    },
    isAppLockEnabled() {
      return this.$store.getters['appLock/isAppLockEnabled'];
    }
  },
  watch: {
    async isAppLockEnabled(enabled) {
      if (!Capacitor.isNativePlatform()) {
        return;
      }
      if (enabled) {
        await inactivityTimer.start(() => {
          this.$store.commit('appLock/setLocked', true);
        });
      } else {
        inactivityTimer.stop();
      }
    }
  },
  async mounted() {
    this.$router.onReady(() => {
      this.routerReady = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!Capacitor.isNativePlatform()) {
            document
              .querySelectorAll('#safe-area-bottom, #safe-area-top')
              .forEach(el => el.remove());
          }
          document.body.style.visibility = 'visible';
        });
      });
    });

    if (Capacitor.isNativePlatform()) {
      this.$el.classList.add('app-container-capacitor');
      await this.initAppLock();
    }
  },
  beforeDestroy() {
    inactivityTimer.stop();
  },
  methods: {
    async initAppLock() {
      const { loadState } = useAppLock();
      const { biometryEnabled, pinEnabled } = await loadState();
      this.$store.commit('appLock/setBiometryEnabled', biometryEnabled);
      this.$store.commit('appLock/setPinEnabled', pinEnabled);
      // Lock only when the session is still alive — if tokens expired while
      // the app was away, auth/accessToken is already empty (AuthVuexPlugin
      // clears it synchronously before mounted runs) and locking a logged-out
      // user would show the lock screen over the login page.
      if ((biometryEnabled || pinEnabled) && this.$store.state.auth.accessToken) {
        this.$store.commit('appLock/setLocked', true);
      }
    }
  }
});
</script>

<style scoped lang="scss">
.app-version {
  display: none;
}
</style>
